import { trackError } from './helpers';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'http://localhost:10123' 
    : 'https://api.spanska-nyheter.se');

// Fetch articles with optional offset, limit, region and category filters
export const fetchArticles = async (offset = 0, limit = 20, selectedRegions = [], excludedCategories = []) => {
  try {
    const queryParams = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    });
    
    // Add region filtering
    // If no regions selected, show all
    // If some regions selected, filter to those
    if (selectedRegions && selectedRegions.length > 0 && selectedRegions.length < 22) {
      // Less than all regions selected, so apply filter
      queryParams.append('included_regions', selectedRegions.join(','));
    }
    
    // Add category filtering
    // Categories are excluded (backend uses excluded_categories)
    if (excludedCategories && excludedCategories.length > 0) {
      queryParams.append('excluded_categories', excludedCategories.join(','));
    }
    
    const response = await fetch(`${API_URL}/rss-articles?${queryParams}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching articles:', error);
    trackError('api-fetch-articles-error', error, {
      offset,
      limit,
      regionCount: selectedRegions?.length || 0,
      excludedCategoryCount: excludedCategories?.length || 0,
      endpoint: 'rss-articles'
    });
    throw error;
  }
};

// Fetch single article by ID
export const fetchArticleById = async (id) => {
  try {
    // Guard clause: Validate ID
    if (!id || id === 'undefined' || id === 'null') {
      console.error('fetchArticleById: Invalid article ID:', id);
      return null;
    }
    
    const response = await fetch(`${API_URL}/rss-articles/${id}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching article:', error);
    trackError('api-fetch-article-by-id-error', error, {
      articleId: id,
      endpoint: `rss-articles/${id}`
    });
    throw error;
  }
};

// Fetch followup answer for article
export const fetchFollowupAnswer = async (articleId, questionIndex) => {
  try {
    // Guard clause: Validate ID
    if (!articleId || articleId === 'undefined' || articleId === 'null') {
      console.error('fetchFollowupAnswer: Invalid article ID:', articleId);
      throw new Error('Invalid article ID');
    }
    
    const response = await fetch(`${API_URL}/rss-articles/${articleId}/followup/${questionIndex}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching followup answer:', error);
    trackError('api-fetch-followup-error', error, {
      articleId,
      questionIndex,
      endpoint: `rss-articles/${articleId}/followup/${questionIndex}`
    });
    throw error;
  }
};

// Fetch article sources (original + duplicates)
export const fetchArticleSources = async (articleId) => {
  try {
    // Guard clause: Validate ID
    if (!articleId || articleId === 'undefined' || articleId === 'null') {
      console.error('fetchArticleSources: Invalid article ID:', articleId);
      return [];
    }
    
    const response = await fetch(`${API_URL}/rss-articles/${articleId}/sources`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.sources || [];
  } catch (error) {
    console.error('Error fetching article sources:', error);
    trackError('api-fetch-sources-error', error, {
      articleId,
      endpoint: `rss-articles/${articleId}/sources`
    });
    return [];
  }
};

// Fetch available regions
export const fetchRegions = async () => {
  try {
    const response = await fetch(`${API_URL}/regions`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching regions:', error);
    trackError('api-fetch-regions-error', error, {
      endpoint: 'regions',
      usingFallback: true
    });
    // Return fallback regions if API fails
    return [
      "Andalusien",
      "Aragonien",
      "Asturien",
      "Balearerna",
      "Baskien",
      "Ceuta",
      "Extremadura",
      "Galicien",
      "Kanarieöarna",
      "Kantabrien",
      "Kastilien-La Mancha",
      "Kastilien och León",
      "Katalonien",
      "La Rioja",
      "Madrid",
      "Melilla",
      "Murcia",
      "Navarra",
      "Valencia",
      "Spanien",
      "Internationellt"
    ];
  }
};

// Fetch available categories
export const fetchCategories = async () => {
  try {
    const response = await fetch(`${API_URL}/rss-categories`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    trackError('api-fetch-categories-error', error, {
      endpoint: 'rss-categories',
      usingFallback: true
    });
    // Return fallback categories if API fails
    return [
      "nyheter",
      "sport",
      "ekonomi",
      "kultur",
      "vetenskap",
      "hälsa",
      "teknik",
      "miljö",
      "politik",
      "nöje",
      "resor",
      "mat & dryck"
    ];
  }
};
